import React, { useEffect, useState } from 'react';
import {
  Page,
  Popup,
  Navbar,
  Block,
  Row,
  Col,
  Button,
  NavRight,
  Link
} from 'framework7-react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import II18 from '../../../translate/II18';
import RegistrationForm from '../RegistrationForm';
import { signmeRegisterFormStructure } from './signmeRegisterFormStructure';
import { findAllFormErrors } from '../../../helpers/formUtils';
import { useRegistrationData } from '../useRegistrationData';
import QesSuccessFooter from '../QesSuccessFooter';
import { dispatchCompletePreviewQes } from '../completePreviewQes';

/**
 * Preview-only Sign-me dialog: registration form then fake QES success.
 * Shares RegistrationForm with the production popup.
 */
export default function SignmeRegisterPopUpPreview(props) {
  const {
    opened,
    onClose,
    noanimate,
    docId,
    signatureFieldId,
    currentRecipientData,
    documentIsQualifiedSigned,
    onSignFlowDone,
    onRegistrationCancelled
  } = props;

  const dispatch = useDispatch();
  const { registrationData } = useRegistrationData(
    signatureFieldId,
    signmeRegisterFormStructure,
    currentRecipientData
  );
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [phase, setPhase] = useState('form');

  useEffect(() => {
    if (!opened) {
      setShowFormErrors(false);
      setPhase('form');
    }
  }, [opened]);

  useEffect(() => {
    if (opened && documentIsQualifiedSigned) {
      setPhase('success');
    }
  }, [opened, documentIsQualifiedSigned]);

  const handleClose = () => {
    if (onRegistrationCancelled) {
      onRegistrationCancelled();
    } else if (onClose) {
      onClose();
    }
  };

  const applyChanges = () => {
    const errors = findAllFormErrors(
      [signmeRegisterFormStructure],
      registrationData
    );
    if (errors.size) {
      setShowFormErrors(true);
      return;
    }
    dispatchCompletePreviewQes(dispatch, docId);
  };

  return (
    <Popup
      id="signme_register_popup_preview"
      className="signme_register_popup"
      opened={Boolean(opened)}
      onPopupClosed={handleClose}
      swipeToClose={false}
      closeByBackdropClick={false}
      closeByOutsideClick={false}
      animate={!noanimate}
    >
      <Page style={{ background: '#fff' }}>
        <Navbar title={II18.get('dialog_signme_settings_title')}>
          <NavRight>
            <Link popupClose onClick={handleClose}>
              <II18>title_close</II18>
            </Link>
          </NavRight>
        </Navbar>

        {phase === 'success' ? (
          <Block
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingTop: 40
            }}
          >
            <h3>
              <II18>esignature_document_signed</II18>
            </h3>
            <QesSuccessFooter
              onDone={() => {
                if (onSignFlowDone) onSignFlowDone();
                else if (onClose) onClose();
              }}
            />
          </Block>
        ) : (
          <Block
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxSizing: 'border-box'
            }}
          >
            <div
              style={{
                flexGrow: 1,
                overflow: 'hidden',
                overflowY: 'auto',
                padding: 10,
                height: '100%'
              }}
              className="tinny-scrollbar"
            >
              <RegistrationForm
                signatureFieldId={signatureFieldId}
                signatureFields={signmeRegisterFormStructure}
                showFormErrors={showFormErrors}
                currentRecipientData={currentRecipientData}
              />
            </div>
            <Block>
              <Row>
                <Col>
                  <Button onClick={handleClose}>
                    {II18.get('title_cancel')}
                  </Button>
                </Col>
                <Col>
                  <Button onClick={applyChanges} raised fill>
                    {II18.get('title_next')}
                  </Button>
                </Col>
              </Row>
            </Block>
          </Block>
        )}
      </Page>
    </Popup>
  );
}

SignmeRegisterPopUpPreview.propTypes = {
  noanimate: PropTypes.bool,
  opened: PropTypes.any,
  docId: PropTypes.string,
  signatureFieldId: PropTypes.string,
  onClose: PropTypes.func,
  onRegistrationCancelled: PropTypes.func,
  currentRecipientData: PropTypes.object,
  documentIsQualifiedSigned: PropTypes.bool,
  onSignFlowDone: PropTypes.func
};
